import type { TaskListType } from "@orsane/schema/task-lists";
import { BuiltInAttribute } from "@orsane/schema/tasks";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { trpc } from "@/components/QueryProvider";
import { authClient } from "@/lib/auth";
import { trpcClient, useTRPC } from "@/lib/trpc";
import type { Attribute } from "@/types/attribute";
import queryKeys from "../app/query-keys";

export const attributesQueryOptions = (taskListId: number | undefined, type: TaskListType) =>
    queryOptions<Attribute[]>({
        queryKey: queryKeys.attributes(taskListId, type),
        queryFn: async () => {
            const hardcodedAttributes = [
                {
                    id: BuiltInAttribute.taskStatus,
                    name: "Status",
                    type: "builtin",
                },
                { id: BuiltInAttribute.id, name: "ID", type: "builtin" },
                {
                    id: BuiltInAttribute.createdAt,
                    name: "Created date",
                    type: "builtin",
                },
                {
                    id: BuiltInAttribute.dueDate,
                    name: "Due date",
                    type: "builtin",
                },
                {
                    id: BuiltInAttribute.priority,
                    name: "Priority",
                    type: "builtin",
                },
                {
                    id: BuiltInAttribute.assignee,
                    name: "Assignee",
                    type: "builtin",
                },
                {
                    id: BuiltInAttribute.createdBy,
                    name: "Created by",
                    type: "builtin",
                },
                { id: BuiltInAttribute.lists, name: "Lists", type: "builtin" },
                { id: BuiltInAttribute.parentTask, name: "Parent task", type: "builtin" },
            ];

            const customAttributes = await trpcClient.getAttributes.query();
            return [
                ...hardcodedAttributes,
                ...customAttributes.map((x) => ({
                    id: x.id,
                    name: x.name,
                    type: x.type,
                })),
            ];
        },
    });

export const sessionQueryOptions = () =>
    queryOptions({
        queryKey: ["session"],
        queryFn: () => authClient.getSession(),
        staleTime: 1000 * 60 * 60 * 4, // 4 hours
        gcTime: 1000 * 60 * 60 * 4, // 4 hours
        select: (data) => data?.data,
    });

export const useSessionQuery = () => {
    const q = useQuery(sessionQueryOptions());
    return q;
};

export const searchQueryOptions = ({ open, query }: { open: boolean; query: string }) => {
    const trpc = useTRPC();
    const opts = trpc.search.search.queryOptions(
        { query },
        {
            enabled: open && query.length > 0,
        }
    );
    return opts;
};

export const useUsers = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const trpc = useTRPC();
    const opts = trpc.user.list.queryOptions(undefined, {
        enabled: enabled,
        staleTime: 1000 * 60, // 1 minute
        gcTime: 1000 * 60,
    });
    const q = useQuery(opts);

    return q;
};

export const orbitSupportProjectQueryOptions = (search: string) =>
    trpc.orbit.listSupportProjects.queryOptions(
        { search },
        {
            staleTime: Infinity,
            gcTime: Infinity,
        }
    );

export const customAttributesQueryOptions = () => {
    return trpc.customAttributes.list.queryOptions(undefined);
};

export const taskStatusesQueryOptions = () => {
    return trpc.taskStatus.list.queryOptions(undefined);
};
